import { displayPrice } from '../displayRounding';

const formatChargeLabel = (text) =>
    text?.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Builds product and additional-charge rows for the "Product Price Breakdown" popup.
 *
 * Unit price uses the supplier early-payment-adjusted line total:
 *   unitPrice = (total_price - total_price_early_payment) / volume
 *
 * Only the early payment charge is deducted from the product line total; platform fees,
 * termsheet fees, and additional charges are surfaced as separate rows, not folded into $/MT.
 *
 * @param {object|null|undefined} dealData - `/dealcalculation` response (state.transaction.dealCalculation)
 * @returns {{ products: Array<{ productName: string, unitPrice: number|null }>, additionalCharges: Array<{ label: string, amount: number }> }}
 */
export const buildProductPriceBreakdownRows = (dealData) => {
    if (!dealData) {
        return { products: [], additionalCharges: [] };
    }

    const products = Array.isArray(dealData.products) ? dealData.products : [];
    const charges = Array.isArray(dealData.additional_charges) ? dealData.additional_charges : [];

    const productRows = products.map((product) => {
        const volume = Number(product?.volume);
        const totalPrice = Number(product?.total_price) || 0;
        const totalEarlyPayment = Number(product?.total_price_early_payment) || 0;

        const unitPrice =
            Number.isFinite(volume) && volume > 0
                ? Number(displayPrice((totalPrice - totalEarlyPayment) / volume))
                : null;

        return {
            productName: product?.product_name ? product.product_name : 'Product',
            unitPrice,
        };
    });

    const chargeRows = charges
        .filter((charge) => charge?.charge_type && charge.charge_type !== '')
        .map((charge) => ({
            label: charge.other_charge_name
                ? `${formatChargeLabel(charge.charge_type)} (${charge.other_charge_name})`
                : formatChargeLabel(charge.charge_type),
            amount: Number(charge.charge_amount) || 0,
        }));

    return { products: productRows, additionalCharges: chargeRows };
};

export default buildProductPriceBreakdownRows;
